import React, { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
import './Contributions.css';

const Contributions = () => {
    const { user, hasRole } = useAuth();
    const { t } = useLanguage();
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [summary, setSummary] = useState([]);
    const [memberSummary, setMemberSummary] = useState({});

    const [deductionRate, setDeductionRate] = useState(4); // default 4%
    const [payrollFile, setPayrollFile] = useState(null);
    const [showImport, setShowImport] = useState(false);
    const [, setImportResult] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [accounts,setAccounts]=useState([]);
    const [posting,setPosting]=useState(null);
    const [postingForm,setPostingForm]=useState({cash_account_id:'',income_account_id:''});
    const [error,setError]=useState('');

    useEffect(() => {
        // Members see only their own contributions; privileged users see the whole list
        if (user?.role === 'member') {
            setLoading(true);
            api.getMyMember()
                .then(res => {
                    // Handle different response structures
                    const memberData = res?.data?.member || res?.data || res;
                    const memberId = memberData?.id;
                    if (!memberId) {
                        console.log('No linked member profile, response:', res);
                        throw new Error('No linked member profile');
                    }
                    return api.getMemberContributions(memberId);
                })
                .then(memberContribs => {
                    console.log('Member contributions response:', memberContribs);
                    // Handle different response structures
                    const contribData = memberContribs?.data || memberContribs;
                    setContributions(contribData?.contributions || contribData || []);
                    setMemberSummary(contribData?.summary || {});
                })
                .catch(err => {
                    console.error('Error loading member contributions:', err);
                    setContributions([]);
                    setMemberSummary({});
                })
                .finally(() => setLoading(false));
        } else {
            loadContributions();
            api.getAccounts().then(r=>setAccounts(r.data||[])).catch(()=>setAccounts([]));
        }
    }, [month, year, user]);

    const loadContributions = async () => {
        setLoading(true);
        try {
            const [contribs, monthly] = await Promise.all([
                api.getContributions({ month, year }),
                api.getMonthlyContributionsSummary(year)
            ]);
            setContributions(contribs.data);
            setSummary(Array.isArray(monthly.data) ? monthly.data : []);
        } catch (error) {
            console.error('Error loading contributions:', error);
            setContributions([]);
            setSummary([]);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const processPayroll = async () => {
        if (window.confirm(t('Process payroll deductions for this month?'))) {
            try {
                await api.processPayrollDeduction(month, year, deductionRate);
                loadContributions();
            } catch (error) {
                console.error('Error processing payroll:', error);
            }
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPayrollFile(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                const text = event.target.result;
                const lines = text.split('\n').filter(line => line.trim());
                const data = lines.slice(1).map(line => {
                    const [member_number, amount, period_month, period_year] = line.split(',').map(s => s.trim());
                    return { member_number, amount, period_month, period_year };
                });
                setCsvData(data);
                setShowPreview(true);
            };
            reader.readAsText(file);
        }
    };

    const handleImport = async () => {
        if (!csvData.length) return;
        if (!window.confirm(`${t('Import')} ${csvData.length} ${t('records?')}`)) return;

        setLoading(true);
        try {
            const result = await api.importContributions(payrollFile);
            setImportResult(result);
            setShowImport(false);
            setPayrollFile(null);
            setCsvData([]);
            setShowPreview(false);
            loadContributions();
        } catch (error) {
            console.error('Error importing:', error);
            alert(t('Import failed'));
        } finally {
            setLoading(false);
        }
    };

    const prepareAccounting=async e=>{e.preventDefault();try{setError('');await api.prepareContributionAccounting(posting.id,postingForm);setPosting(null);setPostingForm({cash_account_id:'',income_account_id:''});loadContributions();}catch(err){setError(err.message);}};

    return (
        <div className={`contributions-page ${user?.role === 'member' ? 'member-contributions' : 'staff-contributions'}`}>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('Contributions')}</h1>
                    <span className="page-subtitle">{t('Manage member contributions')}</span>
                </div>
                {hasRole(['admin', 'treasurer']) && (
                    <div style={{display: 'flex', gap: '10px'}}>
                        <button className="btn btn-secondary" onClick={() => setShowImport(!showImport)}>
                            📥 {t('Import CSV')}
                        </button>
                        <button className="btn btn-primary" onClick={processPayroll}>
                            ⚙️ {t('Process Payroll')}
                        </button>
                    </div>
                )}
            </div>

            {/* Month/Year Filter */}
            <div className="filter-bar">
                <div className="filter-group">
                    <label>{t('Month:')}</label>
                    <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
                        {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{new Date(0, m-1).toLocaleString('en', { month: 'long' })}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>{t('Year:')}</label>
                    <input
                        type="number"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value))}
                        min="2020"
                        max="2030"
                    />
                </div>
            </div>
            {error&&<div className="alert alert-danger">{error}</div>}

            {/* Summary Cards - Shows different stats for members vs admins */}
            <div className="stats-grid">
                {user?.role === 'member' ? (
                    // Member sees their personal stats
                    <>
                        <div className="stat-card">
                            <div className="stat-icon">📊</div>
                            <div className="stat-value">
                                {memberSummary.total_months || 0}
                            </div>
                            <div className="stat-label">{t('Total Months')}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">✅</div>
                            <div className="stat-value">
                                {memberSummary.paid_months || 0}
                            </div>
                            <div className="stat-label">{t('Paid Months')}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">💰</div>
                            <div className="stat-value">
                                {formatCurrency(memberSummary.total_paid || 0)}
                            </div>
                            <div className="stat-label">{t('Total Paid')}</div>
                        </div>
                    </>
                ) : (
                    // Admin sees summary stats
                    <>
                        <div className="stat-card">
                            <div className="stat-icon">💰</div>
                            <div className="stat-value">
                                {formatCurrency(summary.filter(s => parseInt(s.period_month) === month).reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0))}
                            </div>
                            <div className="stat-label">{t('Total Collected')}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">✅</div>
                            <div className="stat-value">
                                {summary.filter(s => parseInt(s.period_month) === month).reduce((sum, s) => sum + (parseInt(s.completed) || 0), 0)}
                            </div>
                            <div className="stat-label">{t('Completed')}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon">⏳</div>
                            <div className="stat-value">
                                {summary.filter(s => parseInt(s.period_month) === month).reduce((sum, s) => sum + (parseInt(s.pending) || 0), 0)}
                            </div>
                            <div className="stat-label">{t('Pending')}</div>
                        </div>
                    </>
                )}
            </div>

            {/* CSV Preview */}
            {showPreview && (
                <div className="card" style={{marginTop: '12px', padding: '12px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div>
                            <strong>{t('CSV Preview')}</strong>
                            <span style={{marginLeft: '10px', color: '#666'}}>{csvData.length} {t('records')}</span>
                        </div>
                        <div style={{display: 'flex', gap: '10px'}}>
                            <button className="btn btn-secondary btn-sm" onClick={() => {setShowPreview(false); setPayrollFile(null);}}>{t('Cancel')}</button>
                            <button className="btn btn-primary btn-sm" onClick={handleImport}>{t('Confirm Import')}</button>
                        </div>
                    </div>
                    <table className="data-table" style={{marginTop: '10px', fontSize: '12px'}}>
                        <thead>
                            <tr>
                                <th>{t('Member #')}</th>
                                <th>{t('Amount')}</th>
                                <th>{t('Period Month')}</th>
                                <th>{t('Period Year')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {csvData.slice(0, 5).map((row, i) => (
                                <tr key={i}>
                                    <td>{row.member_number}</td>
                                    <td>{row.amount}</td>
                                    <td>{row.period_month}</td>
                                    <td>{row.period_year}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {csvData.length > 5 && <div style={{padding: '8px', color: '#666', fontSize: '12px'}}>{t('...and')} {csvData.length - 5} {t('more')}</div>}
                </div>
            )}

            {/* Contributions Table */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">
                        {user?.role === 'member' ? t('My Contributions') : `${t('Contributions for')} ${new Date(0, month-1).toLocaleString('en', { month: 'long' })} ${year}`}
                    </h3>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : contributions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">💰</div>
                        <h3 className="empty-state-title">{t('No Contributions Found')}</h3>
                        <p>{t('No contribution records for this period')}</p>
                    </div>
                ) : (<>
                    <div className="contribution-mobile-list">
                        {contributions.map(contrib => <article className="contribution-mobile-card" key={`mobile-${contrib.id}`}>
                            <div className="contribution-mobile-top"><div><small>{contrib.period_month}/{contrib.period_year}</small><h4>{contrib.contribution_type || t('Mchango wa Kawaida')}</h4></div><strong>{formatCurrency(contrib.total_amount || 0)}</strong></div>
                            {user?.role !== 'member' && <div className="contribution-member"><span>{contrib.first_name} {contrib.last_name}</span><small>{contrib.member_number}</small></div>}
                            <div className="contribution-mobile-meta"><span className={`badge ${contrib.payment_status === 'completed' ? 'badge-success' : 'badge-warning'}`}>{contrib.payment_status || 'pending'}</span><span className={`badge ${contrib.accounting_status==='posted'?'badge-success':'badge-warning'}`}>{contrib.accounting_status||'unposted'}</span><time>{contrib.created_at ? new Date(contrib.created_at).toLocaleDateString() : '-'}</time></div>
                            {user?.role!=='member'&&<div className="contribution-mobile-action">{contrib.payment_status==='completed'&&(!contrib.accounting_status||contrib.accounting_status==='unposted')&&<button className="btn btn-primary btn-sm" onClick={()=>setPosting(contrib)}>{t('Prepare receipt')}</button>}{contrib.receipt_id&&<span>{t('Receipt #')}{contrib.receipt_id}</span>}</div>}
                        </article>)}
                    </div>
                    <table className="data-table contributions-table">
                        <thead>
                            <tr>
                                <th>{t('Member #')}</th>
                                <th>{t('Name')}</th>
                                <th>{t('Type')}</th>
                                <th>{t('Amount')}</th>
                                <th>{t('Month')}</th>
                                <th>{t('Status')}</th>
                                <th>{t('Accounting')}</th><th>{t('Date')}</th>{user?.role!=='member'&&<th>{t('Action')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {contributions.map((contrib) => (
                                <tr key={contrib.id}>
                                    <td>{contrib.member_number}</td>
                                    <td>{contrib.first_name} {contrib.last_name}</td>
                                    <td>
                                        <span className="badge badge-info">
                                            {contrib.contribution_type || t('Mchango wa Kawaida')}
                                        </span>
                                    </td>
                                    <td>{formatCurrency(contrib.total_amount || 0)}</td>
                                    <td>{contrib.period_month}/{contrib.period_year}</td>
                                    <td>
                                        <span className={`badge ${contrib.payment_status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                                            {contrib.payment_status || 'pending'}
                                        </span>
                                    </td>
                                    <td><span className={`badge ${contrib.accounting_status==='posted'?'badge-success':'badge-warning'}`}>{contrib.accounting_status||'unposted'}</span></td><td>{contrib.created_at ? new Date(contrib.created_at).toLocaleDateString() : '-'}</td>{user?.role!=='member'&&<td>{contrib.payment_status==='completed'&&(!contrib.accounting_status||contrib.accounting_status==='unposted')&&<button className="btn btn-primary btn-sm" onClick={()=>setPosting(contrib)}>{t('Prepare receipt')}</button>}{contrib.receipt_id&&<span>{t('Receipt #')}{contrib.receipt_id}</span>}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>)}
            </div>

            {/* Import Modal */}
            {showImport && (
                <div className="modal-overlay" onClick={() => setShowImport(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('Import Contributions CSV')}</h3>
                            <button className="modal-close" onClick={() => setShowImport(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{marginBottom: '16px', color: '#666'}}>
                                {t('Upload a CSV file with columns: member_number, amount, period_month, period_year')}
                            </p>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>
            )}
            {posting&&<div className="modal-overlay" onClick={()=>setPosting(null)}><div className="modal" onClick={e=>e.stopPropagation()}><div className="modal-header"><h3>{t('Prepare contribution receipt')}</h3><button className="modal-close" onClick={()=>setPosting(null)}>×</button></div><form className="modal-body" onSubmit={prepareAccounting}><p>{posting.member_number} · {formatCurrency(posting.total_amount)}</p><label>{t('Cash / Bank account')}<select required className="form-input" value={postingForm.cash_account_id} onChange={e=>setPostingForm({...postingForm,cash_account_id:e.target.value})}><option value="">{t('Choose account')}</option>{accounts.filter(a=>a.status==='active'&&a.account_type==='asset').map(a=><option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}</select></label><label>{t('Contribution income account')}<select required className="form-input" value={postingForm.income_account_id} onChange={e=>setPostingForm({...postingForm,income_account_id:e.target.value})}><option value="">{t('Choose account')}</option>{accounts.filter(a=>a.status==='active'&&['income','liability'].includes(a.account_type)).map(a=><option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}</select></label><button className="btn btn-primary" type="submit">{t('Create & submit receipt')}</button></form></div></div>}
        </div>
    );
};

export default Contributions;
